// frontend/src/features/production/serialization/components/BarcodeScanner.tsx
//
// ADR-104 Rule SN-09: serial number entry must support BOTH typing and
// barcode/QR scanning, with no backend distinction between the two — this
// component is purely a UI convenience that ends up calling the same
// onSubmit(value) either way.

import { useEffect, useRef, useState } from "react";
import QrScanner from "qr-scanner";

interface BarcodeScannerProps {
  label: string;
  placeholder?: string;
  onSubmit: (value: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
}

export function BarcodeScanner({
  label,
  placeholder,
  onSubmit,
  autoFocus = true,
  disabled = false,
}: BarcodeScannerProps) {
  const [value, setValue] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scannerRef = useRef<QrScanner | null>(null);

  useEffect(() => {
    return () => {
      scannerRef.current?.destroy();
      scannerRef.current = null;
    };
  }, []);

  const handleTypedSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    setValue("");
  };

  const openCamera = async () => {
    setCameraError(null);
    setCameraOpen(true);
  };

  useEffect(() => {
    if (!cameraOpen || !videoRef.current) return;

    const scanner = new QrScanner(
      videoRef.current,
      (result) => {
        const text = typeof result === "string" ? result : result.data;
        onSubmit(text.trim());
        closeCamera();
      },
      { highlightScanRegion: true, highlightCodeOutline: true }
    );
    scannerRef.current = scanner;

    scanner.start().catch((err) => {
      setCameraError("دوربین در دسترس نیست — می‌توانید به‌صورت دستی تایپ کنید.");
      setCameraOpen(false);
      // eslint-disable-next-line no-console
      console.warn("BarcodeScanner: camera start failed", err);
    });

    return () => {
      scanner.destroy();
      scannerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOpen]);

  const closeCamera = () => {
    scannerRef.current?.destroy();
    scannerRef.current = null;
    setCameraOpen(false);
  };

  return (
    <div>
      <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
        {label}
      </label>
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleTypedSubmit();
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          dir="ltr"
          style={{ flex: 1, padding: "0.6rem 0.9rem", borderRadius: "8px" }}
        />
        <button
          type="button"
          className="btn btn-secondary"
          onClick={cameraOpen ? closeCamera : openCamera}
          disabled={disabled}
        >
          {cameraOpen ? "بستن دوربین" : "اسکن بارکد"}
        </button>
        <button type="button" className="btn btn-primary" onClick={handleTypedSubmit} disabled={disabled}>
          ثبت
        </button>
      </div>

      {cameraOpen && (
        <div style={{ marginTop: "0.75rem" }}>
          <video ref={videoRef} style={{ width: "100%", maxWidth: "360px", borderRadius: "8px" }} />
        </div>
      )}
      {cameraError && (
        <p style={{ color: "#b91c1c", fontSize: "0.8rem", marginTop: "0.4rem" }}>{cameraError}</p>
      )}
    </div>
  );
}
